import { useState, useEffect, useMemo } from "react";
import { DialogContent, Box } from "@mui/material";
import { Inventory2Outlined } from "@mui/icons-material";
import { Util } from "../../../util/Util";
import {
	MoleDialog,
	MoleDialogHeader,
	useMoleThemeColor,
} from "../../../components/MoleDialogShell";
import { toast } from "react-toastify";
import { useAuth } from "../../../hooks/useAuth";
import { useUI } from "../../../providers/MoleUIContext";
import { NUMBER_FILTER_OPTIONS, TEXT_FILTER_OPTIONS } from "../../../util/constants";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import TableView from "../../../components/TableView";
import { MRT_ColumnDef } from "material-react-table";
import ExportMenuButton from "../../../components/ExportMenuButton";

import { createSignCell } from "../../../util/cellFormatters";


import { roundQty } from "../../../util/quantity";
interface Props {
	isOpen: boolean;
	toggle: () => void;
	bundle: any;
}

const InventoryPointReport = ({ isOpen, toggle, bundle }: Props) => {
	const { api } = useAuth();
	const { setLoadingMessage, queueReRun, t } = useUI();
	const themeColor = useMoleThemeColor();
	const [record, setRecord] = useState<any>(null);


	const fetchData = async () => {
		setLoadingMessage("FETCH");

		const res = await api.get(`inventory/point`, {
			params: {
				...bundle
			}
		});

		if (res) {
			if (res.status === 200) {
				setRecord(res.data);
				setLoadingMessage(null);
				return;
			} else if (res.status === 404) {
				toast.warn(t("common:toast.warn.noRecord"));
			} else if (res.status === 401 || res.status === 422) {
				queueReRun(fetchData);
			} else {
				console.error("Failed to fetch data:", res);
			}
		}
		setLoadingMessage(null);
		toggle();
	};


	useEffect(() => {
		if (isOpen && bundle) {
			fetchData();
		}
	}, [isOpen]);


	// Define table columns
	const columns = useMemo<MRT_ColumnDef<any>[]>(() => [
		{
			header: t("common:tables.sector"),
			accessorKey: "sector",
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.category"),
			accessorKey: "category",
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.itemCode"),
			accessorKey: "item_code",
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.item"),
			accessorKey: "item_name",
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.startingDate"),
			id: "starting_date",
			accessorFn: (row: any) => new Date(row.starting_date),
			Cell: ({ cell }: { cell: any }) => Util.formatDate(cell.getValue(), "medium"), //render Date as a string
			filterVariant: 'date-range',
		},
		{
			header: t("common:tables.startingQuantity"),
			accessorFn: (row: any) => Number(row.starting_qty),
			Cell: ({ cell }: { cell: any }) => Util.formatNumber(cell.getValue()),
			filterFn: 'contains',
			columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.netQtyChange"),
			accessorFn: (row: any) => Number(row.net_change),
			Cell: createSignCell(),
			filterFn: 'contains',
			columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.stockLevel"),
			accessorFn: (row: any) => Number(row.stock_level),
			Cell: ({ cell }: { cell: any }) => Util.formatNumber(cell.getValue()),
			filterFn: 'contains',
			columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.costPrice"),
			accessorFn: (row: any) => Number(row.cost_price),
			Cell: ({ cell }: { cell: any }) => Util.formatNumber(cell.getValue()),
			filterFn: 'contains',
			columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.total"),
			accessorFn: (row: any) => Number(row.total),
			Cell: ({ cell }: { cell: any }) => Util.formatNumber(cell.getValue()),
			filterFn: 'contains',
			columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
			aggregationFn: 'sum', //show the max age in the group (lots of pre-built aggregationFns to choose from)
			//required to render an aggregated cell
			AggregatedCell: ({ cell, table }) => (
				<>
					{t("common:tableView.sumBy")}{' '}
					{table.getColumn(cell.row.groupingColumnId ?? '').columnDef.header}:{' '}
					<Box
						sx={{ color: 'info.main', display: 'inline', fontWeight: 'bold' }}
					>
						{Util.formatNumber(cell.getValue<number>())}
					</Box>
				</>
			),
			// Add Footer for Grand Total
			Footer: ({ table }) => {
				const [total, setTotal] = useState<number>(0);
				const [isCalculating, setIsCalculating] = useState(false);

				useEffect(() => {
					setIsCalculating(true);
					setTotal(0);

					const rows = table.getFilteredRowModel().rows;
					// run in a micro delay, allowing paint before heavy computation
					const handle = setTimeout(() => {
						let sum = 0;
						for (let i = 0; i < rows.length; i++) {
							const value = Number(rows[i].original.total);
							if (Number.isFinite(value)) sum += value;
						}
						setTotal(roundQty(sum));
						setIsCalculating(false);
					}, 0); // <— yields to browser first

					return () => clearTimeout(handle);
				}, [table.getFilteredRowModel().rows]);


				return (
					<Box sx={{ fontWeight: 'bold', color: 'success.main' }}>
						{t("common:report.total")}: {Util.formatNumber(total)}
					</Box>
				);
			},
		},
		{
			header: t("common:tables.movementCount"),
			accessorFn: (row: any) => Number(row.movement_count),
			Cell: ({ cell }: { cell: any }) => Util.formatNumber(cell.getValue()),
			filterFn: 'contains',
			columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
		}
	], [t]);

	// A run covers one depot, a few, or every stock-tracking one: name a single
	// depot, count several, and keep the export file name a file name.
	const covered: string[] = record?.details?.depots || [];
	const depotLabel = covered.length > 1
		? t("common:inventory.depotCount", { count: covered.length })
		: (record?.details?.depot || "");
	const depotSlug = depotLabel.replace(/[^\p{L}\p{N}]+/gu, "_").replace(/^_|_$/g, "") || "all";

	return (
		<>
			<MoleDialog
				open={isOpen}
				onClose={toggle}
				maxWidth="xl"
				fullWidth
				themeColor={themeColor}
				paperOptions={{ maxWidth: "95%", width: "95%", maxHeight: "95vh" }}
			>
				<MoleDialogHeader
					title={t("common:inventory.headers.pointInTime", { depot: depotLabel, date: record?.details?.date })}
					icon={<Inventory2Outlined />}
					onClose={toggle}
					themeColor={themeColor}
				/>
				<DialogContent>
					<Box >
						{/* TODO */}
					</Box>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<TableView
							data={record?.records || []}
							columns={columns}
							includeTimestampColumns={false}
							totals={[{ key: "total", label: t("common:tables.total") }]}
							bundle={bundle}
							enableRowActions={false}

							renderTopToolbarActions={(table) => (
								<Box sx={{ display: 'flex', gap: '0.5rem' }}>
									<ExportMenuButton
										prefix={`inventory_${depotSlug}_${bundle.Date}_${bundle.InvType}`}
										data={record?.records || []}
										columns={columns}
										table={table}
									/>
								</Box>
							)}
						/>

					</LocalizationProvider>
				</DialogContent>
			</MoleDialog>

		</>
	);
};

export default InventoryPointReport;
